/**
 * 项目物料页面 - 业务逻辑 Hook
 *
 * 职责：
 * 1. 页面初始化（加载项目列表、默认选中第一个项目并加载物料数据）
 * 2. 筛选查询（对接 SearchFilter 组件）
 * 3. 分页切换
 * 4. 导出数据
 * 5. 审核记录弹框管理
 */
import { useEffect, useReducer, useCallback } from 'react'
import { message } from 'antd'
import { isResponseCodeSuccess } from '@/tools/util'
import reducer, {
  initialState,
  setProjectListAction,
  setSelectedProjectCodeAction,
  setSearchParamAction,
  setDataSourceAction,
  setIsTableLoadingAction,
  setPaginationAction,
  setAuditDrawerVisibleAction,
  setCurrentAuditMaterialAction,
  setAuditRecordsAction,
  setIsAuditLoadingAction,
} from './reducer'
import { GET_PROJECT_LIST } from '@/api/MaterialApi'
import {
  POST_PROJECT_MATERIAL_PAGE,
  POST_PROJECT_MATERIAL_AUDIT_RECORDS,
  EXPORT_PROJECT_MATERIAL,
} from '@/api/projectMaterialApi'
import type {
  ProjectMaterialDTO,
  ProjectMaterialPageParams,
} from '@/api/projectMaterialApi'

/** SearchFilter 可能带上 schemeId；空串、空数组不宜作为查询条件传给物料接口 */
const sanitizeFilters = (raw: Record<string, any>) => {
  const { schemeId: _, ...rest } = raw
  return Object.fromEntries(
    Object.entries(rest).map(([k, v]) => {
      if (v === '' || (Array.isArray(v) && v.length === 0)) {
        return [k, undefined]
      }
      return [k, v]
    }),
  )
}

export interface Props {}

export default function useHook(props: Props) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { selectedProjectCode, searchParam, pagination, currentAuditMaterial } =
    state

  // ======================== 页面初始化 ========================

  useEffect(() => {
    loadProjectList()
  }, [])

  /**
   * 加载项目列表（调用真实接口 GET_PROJECT_LIST）
   * 接口返回 { platformCode, platformName }[]，映射为页面使用的 { projectCode, projectName }
   * 默认选中第一个项目，并加载该项目的物料数据
   */
  const loadProjectList = async () => {
    dispatch(setIsTableLoadingAction(true))
    try {
      const res = await GET_PROJECT_LIST()
      if (isResponseCodeSuccess(res.code)) {
        const list = (res.data || []).map((item) => ({
          projectCode: item.platformCode,
          projectName: item.platformName,
        }))
        dispatch(setProjectListAction(list))
        if (list.length > 0) {
          const firstProjectCode = list[0].projectCode
          dispatch(setSelectedProjectCodeAction(firstProjectCode))
          await loadMaterialData({
            platformCode: firstProjectCode,
            page: 1,
            limit: pagination.pagesize,
          })
        }
      } else {
        message.warning(res.msg || '获取项目列表失败')
      }
    } finally {
      dispatch(setIsTableLoadingAction(false))
    }
  }

  // ======================== 物料列表数据加载 ========================

  /**
   * 加载项目物料分页数据
   * 调用 POST /projectMaterial/getPage
   * 响应格式：PageResultProjectMaterialPageDTO { content, totalCount, pageNo, pageSize, totalPages }
   *
   * @param params 查询参数（包含 platformCode + 筛选条件 + 分页）
   */
  const loadMaterialData = async (params: ProjectMaterialPageParams) => {
    dispatch(setIsTableLoadingAction(true))

    try {
      const res = await POST_PROJECT_MATERIAL_PAGE({ ...params })
      if (isResponseCodeSuccess(res.code)) {
        const pageData = res.data || {}
        dispatch(setDataSourceAction(pageData.content || []))
        dispatch(
          setPaginationAction({
            total: pageData.totalCount || 0,
            current: params.page || 1,
            pagesize: params.limit || pagination.pagesize,
          }),
        )
      } else {
        message.warning(res.msg || '查询物料列表失败')
      }
    } finally {
      dispatch(setIsTableLoadingAction(false))
    }
  }

  // ======================== 项目切换 ========================

  /**
   * 切换项目
   * 切换后清空筛选条件，重新加载该项目的物料列表
   */
  const handleProjectChange = useCallback(
    (projectCode: string) => {
      dispatch(setSelectedProjectCodeAction(projectCode))
      dispatch(setSearchParamAction({}))
      loadMaterialData({
        platformCode: projectCode,
        page: 1,
        limit: pagination.pagesize,
      })
    },
    [pagination.pagesize],
  )

  // ======================== 筛选查询 ========================

  /**
   * SearchFilter 组件的 onSearch 回调
   * 筛选项 form 字段名与 projectMaterial 分页接口一致（productCodes、thirdOnShelf 等）
   *
   * @param formValues SearchFilter 收集的筛选值
   */
  const handleSearch = useCallback(
    (formValues: Record<string, any>) => {
      // 项目必填校验
      if (!selectedProjectCode) {
        message.warning('请先选择项目')
        return
      }
      dispatch(setSearchParamAction(formValues))

      const apiParams: ProjectMaterialPageParams = {
        ...sanitizeFilters(formValues),
        platformCode: selectedProjectCode,
        page: 1,
        limit: pagination.pagesize,
      }

      loadMaterialData(apiParams)
    },
    [selectedProjectCode, pagination.pagesize],
  )

  // ======================== 分页 ========================

  /**
   * 分页切换
   * @param page 目标页码
   * @param pageSize 每页条数
   */
  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      const apiParams: ProjectMaterialPageParams = {
        ...sanitizeFilters(searchParam),
        platformCode: selectedProjectCode,
        page,
        limit: pageSize ?? pagination.pagesize,
      }
      loadMaterialData(apiParams)
    },
    [searchParam, selectedProjectCode, pagination.pagesize],
  )

  // ======================== 刷新 ========================

  /** 刷新当前列表（保留当前筛选条件和页码） */
  const handleRefresh = useCallback(() => {
    const apiParams: ProjectMaterialPageParams = {
      ...sanitizeFilters(searchParam),
      platformCode: selectedProjectCode,
      page: pagination.current,
      limit: pagination.pagesize,
    }
    loadMaterialData(apiParams)
  }, [searchParam, selectedProjectCode, pagination])

  // ======================== 导出 ========================

  /** 导出数据 - TODO: 待后端提供导出接口后对接 */
  const handleExport = useCallback(async () => {
    if (!selectedProjectCode) {
      message.warning('请先选择项目')
      return
    }

    const res = await EXPORT_PROJECT_MATERIAL({
      ...sanitizeFilters(searchParam),
      platformCode: selectedProjectCode,
    })
    if (isResponseCodeSuccess(res.code)) {
      message.success('已转移至任务中心执行')
    } else {
      message.warning(res.msg)
    }
  }, [searchParam, selectedProjectCode])

  // ======================== 审核记录弹框 ========================

  /**
   * 打开审核记录弹框
   * 调用 POST /open/srm/projectMaterial/getAuditRecords?platformCode=X&productCode=Y
   *
   * @param record 当前行物料数据
   */
  const handleViewAuditRecord = useCallback(
    (record: ProjectMaterialDTO) => {
      dispatch(setCurrentAuditMaterialAction(record))
      dispatch(setAuditDrawerVisibleAction(true))
      loadAuditRecords(selectedProjectCode, record.productCode)
    },
    [selectedProjectCode],
  )

  /**
   * 加载审核记录数据（非分页，一次性返回全部记录）
   */
  const loadAuditRecords = async (
    platformCode: string,
    productCode: string,
  ) => {
    dispatch(setIsAuditLoadingAction(true))
    try {
      const res = await POST_PROJECT_MATERIAL_AUDIT_RECORDS({
        platformCode,
        productCode,
      })
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setAuditRecordsAction(res.data || []))
      }
    } finally {
      dispatch(setIsAuditLoadingAction(false))
    }
  }

  /** 关闭审核记录弹框 */
  const handleCloseAuditDrawer = useCallback(() => {
    dispatch(setAuditDrawerVisibleAction(false))
    dispatch(setCurrentAuditMaterialAction(null))
    dispatch(setAuditRecordsAction([]))
  }, [])

  // ======================== 返回值 ========================

  return {
    state,
    handleProjectChange,
    handleSearch,
    handlePaginationChange,
    handleRefresh,
    handleExport,
    handleViewAuditRecord,
    handleCloseAuditDrawer,
  }
}
/**
 * 项目物料页面 - 业务逻辑 Hook
 *
 * 职责：
 * 1. 页面初始化（加载项目列表、默认选中第一个项目并加载物料数据）
 * 2. 筛选查询（对接 SearchFilter 组件）
 * 3. 分页切换
 * 4. 导出数据
 * 5. 审核记录弹框管理
 */
import { useEffect, useReducer, useCallback } from 'react'
import { message } from 'antd'
import { isResponseCodeSuccess } from '@/tools/util'
import reducer, {
  initialState,
  setProjectListAction,
  setSelectedProjectCodeAction,
  setSearchParamAction,
  setDataSourceAction,
  setIsTableLoadingAction,
  setPaginationAction,
  setAuditDrawerVisibleAction,
  setCurrentAuditMaterialAction,
  setAuditRecordsAction,
  setIsAuditLoadingAction,
} from './reducer'
import { GET_PROJECT_LIST } from '@/api/MaterialApi'
import {
  POST_PROJECT_MATERIAL_PAGE,
  POST_PROJECT_MATERIAL_AUDIT_RECORDS,
  EXPORT_PROJECT_MATERIAL,
} from '@/api/projectMaterialApi'
import type {
  ProjectMaterialDTO,
  ProjectMaterialPageParams,
} from '@/api/projectMaterialApi'

/** SearchFilter 可能带上 schemeId；空串、空数组不宜作为查询条件传给物料接口 */
const sanitizeFilters = (raw: Record<string, any>) => {
  const { schemeId: _, ...rest } = raw
  return Object.fromEntries(
    Object.entries(rest).map(([k, v]) => {
      if (v === '' || (Array.isArray(v) && v.length === 0)) {
        return [k, undefined]
      }
      return [k, v]
    }),
  )
}

export interface Props {}

export default function useHook(props: Props) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { selectedProjectCode, searchParam, pagination, currentAuditMaterial } =
    state

  // ======================== 页面初始化 ========================

  useEffect(() => {
    loadProjectList()
  }, [])

  /**
   * 加载项目列表（调用真实接口 GET_PROJECT_LIST）
   * 接口返回 { platformCode, platformName }[]，映射为页面使用的 { projectCode, projectName }
   * 默认选中第一个项目，并加载该项目的物料数据
   */
  const loadProjectList = async () => {
    dispatch(setIsTableLoadingAction(true))
    try {
      const res = await GET_PROJECT_LIST()
      if (isResponseCodeSuccess(res.code)) {
        const list = (res.data || []).map((item) => ({
          projectCode: item.platformCode,
          projectName: item.platformName,
        }))
        dispatch(setProjectListAction(list))
        if (list.length > 0) {
          const firstProjectCode = list[0].projectCode
          dispatch(setSelectedProjectCodeAction(firstProjectCode))
          await loadMaterialData({
            platformCode: firstProjectCode,
            page: 1,
            limit: pagination.pagesize,
          })
        }
      } else {
        message.warning(res.msg || '获取项目列表失败')
      }
    } finally {
      dispatch(setIsTableLoadingAction(false))
    }
  }

  // ======================== 物料列表数据加载 ========================

  /**
   * 加载项目物料分页数据
   * 调用 POST /projectMaterial/getPage
   * 响应格式：PageResultProjectMaterialPageDTO { content, totalCount, pageNo, pageSize, totalPages }
   *
   * @param params 查询参数（包含 platformCode + 筛选条件 + 分页）
   */
  const loadMaterialData = async (params: ProjectMaterialPageParams) => {
    dispatch(setIsTableLoadingAction(true))

    try {
      const res = await POST_PROJECT_MATERIAL_PAGE({ ...params })
      if (isResponseCodeSuccess(res.code)) {
        const pageData = res.data || {}
        dispatch(setDataSourceAction(pageData.content || []))
        dispatch(
          setPaginationAction({
            total: pageData.totalCount || 0,
            current: params.page || 1,
            pagesize: params.limit || pagination.pagesize,
          }),
        )
      } else {
        message.warning(res.msg || '查询物料列表失败')
      }
    } finally {
      dispatch(setIsTableLoadingAction(false))
    }
  }

  // ======================== 项目切换 ========================

  /**
   * 切换项目
   * 切换后清空筛选条件，重新加载该项目的物料列表
   */
  const handleProjectChange = useCallback(
    (projectCode: string) => {
      dispatch(setSelectedProjectCodeAction(projectCode))
      dispatch(setSearchParamAction({}))
      loadMaterialData({
        platformCode: projectCode,
        page: 1,
        limit: pagination.pagesize,
      })
    },
    [pagination.pagesize],
  )

  // ======================== 筛选查询 ========================

  /**
   * SearchFilter 组件的 onSearch 回调
   * 筛选项 form 字段名与 projectMaterial 分页接口一致（productCodes、thirdOnShelf 等）
   *
   * @param formValues SearchFilter 收集的筛选值
   */
  const handleSearch = useCallback(
    (formValues: Record<string, any>) => {
      // 项目必填校验
      if (!selectedProjectCode) {
        message.warning('请先选择项目')
        return
      }
      dispatch(setSearchParamAction(formValues))

      const apiParams: ProjectMaterialPageParams = {
        ...sanitizeFilters(formValues),
        platformCode: selectedProjectCode,
        page: 1,
        limit: pagination.pagesize,
      }

      loadMaterialData(apiParams)
    },
    [selectedProjectCode, pagination.pagesize],
  )

  // ======================== 分页 ========================

  /**
   * 分页切换
   * @param page 目标页码
   * @param pageSize 每页条数
   */
  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      const apiParams: ProjectMaterialPageParams = {
        ...sanitizeFilters(searchParam),
        platformCode: selectedProjectCode,
        page,
        limit: pageSize ?? pagination.pagesize,
      }
      loadMaterialData(apiParams)
    },
    [searchParam, selectedProjectCode, pagination.pagesize],
  )

  // ======================== 刷新 ========================

  /** 刷新当前列表（保留当前筛选条件和页码） */
  const handleRefresh = useCallback(() => {
    const apiParams: ProjectMaterialPageParams = {
      ...sanitizeFilters(searchParam),
      platformCode: selectedProjectCode,
      page: pagination.current,
      limit: pagination.pagesize,
    }
    loadMaterialData(apiParams)
  }, [searchParam, selectedProjectCode, pagination])

  // ======================== 导出 ========================

  /** 导出数据 - TODO: 待后端提供导出接口后对接 */
  const handleExport = useCallback(async () => {
    if (!selectedProjectCode) {
      message.warning('请先选择项目')
      return
    }

    const res = await EXPORT_PROJECT_MATERIAL({
      ...sanitizeFilters(searchParam),
      platformCode: selectedProjectCode,
    })
    if (isResponseCodeSuccess(res.code)) {
      message.success('已转移至任务中心执行')
    } else {
      message.warning(res.msg)
    }
  }, [searchParam, selectedProjectCode])

  // ======================== 审核记录弹框 ========================

  /**
   * 打开审核记录弹框
   * 调用 POST /open/srm/projectMaterial/getAuditRecords?platformCode=X&productCode=Y
   *
   * @param record 当前行物料数据
   */
  const handleViewAuditRecord = useCallback(
    (record: ProjectMaterialDTO) => {
      dispatch(setCurrentAuditMaterialAction(record))
      dispatch(setAuditDrawerVisibleAction(true))
      loadAuditRecords(selectedProjectCode, record.productCode)
    },
    [selectedProjectCode],
  )

  /**
   * 加载审核记录数据（非分页，一次性返回全部记录）
   */
  const loadAuditRecords = async (
    platformCode: string,
    productCode: string,
  ) => {
    dispatch(setIsAuditLoadingAction(true))
    try {
      const res = await POST_PROJECT_MATERIAL_AUDIT_RECORDS({
        platformCode,
        productCode,
      })
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setAuditRecordsAction(res.data || []))
      }
    } finally {
      dispatch(setIsAuditLoadingAction(false))
    }
  }

  /** 关闭审核记录弹框 */
  const handleCloseAuditDrawer = useCallback(() => {
    dispatch(setAuditDrawerVisibleAction(false))
    dispatch(setCurrentAuditMaterialAction(null))
    dispatch(setAuditRecordsAction([]))
  }, [])

  // ======================== 返回值 ========================

  return {
    state,
    handleProjectChange,
    handleSearch,
    handlePaginationChange,
    handleRefresh,
    handleExport,
    handleViewAuditRecord,
    handleCloseAuditDrawer,
  }
}
